// Dashboard / home page.
//
// Layout:
//   - Top row (4 KPI cards): Active Matters · Pipeline Status ·
//     Tasks Due · AI Spend
//   - Stage Funnel + Attorney Workload (two-column operational row)
//   - Quick Actions row
//   - Recent Activity + Recent Matters (two-column row)
//
// Pending users see an "awaiting approval" empty state instead of
// the dashboard — they have no business seeing matter counts or
// activity before a SuperAdmin promotes them.

import React, { useEffect, useMemo, useState } from "react";
import { C, ROLE_BADGE_COLORS, ROLE_LABELS, roleAtLeast } from "../constants.js";
import { I } from "../icons.jsx";
import { cachedFetch } from "../api.js";
import { Badge } from "../components/Badge.jsx";
import { Spinner } from "../components/Spinner.jsx";
import { formatAuditTime, EVENT_TYPE_CONFIG } from "../utils.js";
import { PendingApprovalCard } from "../components/PendingApprovalCard.jsx";

// Maps the pipeline tier names to colors + display labels. Mirrors
// what PipelineDashboardPage uses so the dashboard and the deep
// page agree.
const TIER_STYLES = {
  active: { label: "Active", color: C.success, bg: C.successBg },
  idle:   { label: "Idle",   color: "#2563EB", bg: "#DBEAFE" },
  aging:  { label: "Aging",  color: C.goldDim, bg: `${C.gold}20` },
  stale:  { label: "Stale",  color: C.error,   bg: C.errorBg },
};

// Color palette for stage-funnel bars — cycles through the same
// muted hues used elsewhere so multiple bars don't fight visually.
const STAGE_BAR_COLORS = [
  C.navy, C.gold, "#2563EB", C.success, "#A855F7",
  C.goldDim, "#0891B2", "#DC2626", "#65A30D", "#7C3AED",
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function HomePage({ onNavigate, onMatterClick, authStatus, currentFirm }) {
  const role = authStatus?.firm_user?.role || "pending";
  const isPending = role === "pending";

  const [matters, setMatters] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMatters, setLoadingMatters] = useState(true);

  // Pipeline summary — tier counts + by-stage + by-attorney distributions.
  const [pipeline, setPipeline] = useState(null);

  // AI spend — { last_30_days: { total_cost_usd, ... }, ... }
  const [llmUsage, setLlmUsage] = useState(null);

  // Recent activity — last 10 audit-log entries across all matters
  const [activity, setActivity] = useState(null);

  // Tasks summary — { overdue, due_this_week, total_open, sample[] }
  const [tasks, setTasks] = useState(null);

  // Costs out — Aaron asked for it on the dashboard, openable by
  // attorney the way the workload card is.
  const [tasksOpen, setTasksOpen] = useState(false);
  const [finance, setFinance] = useState(null);
  useEffect(() => {
    cachedFetch("/api/finance/costs")
      .then(setFinance)
      .catch((e) => setFinance({ _error: e.message, totals: {}, by_attorney: [] }));
  }, []);

  useEffect(() => {
    if (isPending) return;
    // All five widgets fetch in parallel. Each is independently
    // cached at the api layer so re-mounting (sidebar nav away +
    // back within 60s) costs nothing.
    cachedFetch("/api/matters")
      .then(data => {
        const all = data.data || [];
        setTotalCount(data.count || all.length);
        // Sort by created_at DESC for "Recent Matters" (Clio
        // pagination forces order=id(asc), so it comes back
        // oldest-first and we re-sort here).
        const recent = [...all].sort((a, b) => {
          const ad = a.created_at ? Date.parse(a.created_at) : 0;
          const bd = b.created_at ? Date.parse(b.created_at) : 0;
          if (bd !== ad) return bd - ad;
          return (b.id || 0) - (a.id || 0);
        }).slice(0, 5);
        setMatters({ all, recent });
        setLoadingMatters(false);
      })
      .catch(() => setLoadingMatters(false));
    cachedFetch("/api/dashboard/pipeline")
      .then(data => setPipeline(data))
      .catch(() => setPipeline({ summary: null, _error: true }));
    cachedFetch("/api/observability/llm-usage")
      .then(data => setLlmUsage(data))
      .catch(() => setLlmUsage({ _error: true }));
    cachedFetch("/api/audit-log?limit=10")
      .then(data => setActivity(data.data || data.entries || []))
      .catch(() => setActivity([]));
    cachedFetch("/api/dashboard/tasks-summary")
      .then(data => setTasks(data))
      .catch(() => setTasks({ _error: true }));
  }, [isPending]);

  // Derive "+N new this week" from the matters list — no extra fetch.
  // open_date is what Clio sets when a matter is created in the firm.
  const newThisWeek = useMemo(() => {
    if (!matters?.all) return null;
    const cutoff = Date.now() - 7 * MS_PER_DAY;
    return matters.all.filter(m => {
      const opened = m.open_date || m.created_at;
      if (!opened) return false;
      return Date.parse(opened) >= cutoff;
    }).length;
  }, [matters]);

  // Grouped here, ABOVE the early returns, because these are hooks.
  //
  // They used to sit further down, after the pending check and the
  // partner gate, so they ran on a partner's render and not on anyone
  // else's. React counts hooks per render and a component that calls
  // three on one pass and five on the next throws — which is what
  // happens the moment a role changes under an open page, or the role
  // simply resolves after the first paint.
  //
  // Key normalization MUST match `_distribution()` in
  // `app/services/pipeline.py`, which collapses empty/whitespace to
  // "(unassigned)". Key differently and clicking an "(unassigned)" bar
  // finds nothing.
  const pipelineMatters = pipeline?.matters || [];
  const _normKey = (v) => (v || "").trim() || "(unassigned)";
  const mattersByStage = useMemo(() => {
    const map = {};
    for (const m of pipelineMatters) {
      const key = _normKey(m.stage);
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    return map;
  }, [pipelineMatters]);
  const mattersByAttorney = useMemo(() => {
    const map = {};
    for (const m of pipelineMatters) {
      const key = _normKey(m.responsible_attorney);
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    return map;
  }, [pipelineMatters]);

  // Pending tier: replace the entire dashboard with an awaiting-
  // approval card.
  // Pending users see this instead of any dashboard — they have no
  // business seeing matter counts before an administrator promotes
  // them. Shared with My Day, which is now the landing page.
  if (isPending) {
    return React.createElement(PendingApprovalCard, { authStatus });
  }

  // Firm-wide numbers — book size, attorney workload, AI spend — are
  // leadership's view. The nav hides this below partner, but hiding a
  // nav item is not a restriction while the page is one hash away, so
  // the page checks for itself.
  //
  // This is the front half only. The endpoints behind it carry no role
  // guard of their own, so anyone signed in can still read the same
  // figures from the API directly.
  if (!roleAtLeast(role, "partner")) {
    return React.createElement("div", {
      className: "fade-in",
      style: { padding: "64px 40px", maxWidth: "560px", margin: "40px auto 0" },
    },
      React.createElement("div", {
        style: {
          background: "#fff", borderRadius: "16px", padding: "40px",
          border: `1px solid ${C.warmGray}`, textAlign: "center",
        },
      },
        React.createElement("h1", {
          style: { fontFamily: "'Playfair Display',serif", fontSize: "22px",
                   color: C.navy, marginBottom: "10px" },
        }, "Firm Overview is for partners and above"),
        React.createElement("p", {
          style: { fontSize: "14px", color: C.textMuted, lineHeight: 1.6 },
        }, "It shows firm-wide figures rather than case work. Your own "
         + "list is on My Day, and the firm's plan of action is on Today."),
      ),
    );
  }
  // ── Dashboard for non-pending users ───────────────────────────
  const summary = pipeline?.summary || {};
  const distributions = pipeline?.distributions || {};
  const monthCost = llmUsage?.last_30_days?.total_cost_usd;
  const monthCalls = llmUsage?.last_30_days?.total_calls;

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1280px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "24px" } },
      React.createElement("h1", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" },
      }, "Firm Overview"),
      React.createElement("p", { style: { fontSize: "15px", color: C.textMuted } },
        `${currentFirm?.display_name || ""}${currentFirm ? " — " : ""}Document Automation Agent`,
      ),
    ),

    // ─── KPI row: 4 cards ─────────────────────────────────────
    React.createElement("div", {
      style: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "20px" },
    },
      _costsOutCard({ finance, onNavigate }),
      _kpiCard({
        label: "Active Matters",
        value: totalCount || (loadingMatters ? "…" : "—"),
        sub: newThisWeek != null
          ? `${newThisWeek} new this week`
          : "Open & Pending",
        onClick: () => onNavigate("matters"),
      }),
      _pipelineCard({
        summary,
        loading: pipeline === null,
        error: pipeline?._error,
        onClick: () => onNavigate("pipeline"),
      }),
      _tasksDueCard({
        data: tasks,
        loading: tasks === null,
        onClick: () => setTasksOpen((v) => !v),
      }),
      _aiSpendCard({
        cost: monthCost,
        calls: monthCalls,
        loading: llmUsage === null,
        error: llmUsage?._error,
      }),
    ),

    // The count, opened up. A number you cannot open is a number with
    // no way to act on it.
    tasksOpen && React.createElement(OutstandingTasksPanel, {
      data: tasks, onMatterClick,
    }),

    // ─── Operational row: Stage Funnel + Attorney Workload ───
    React.createElement("div", {
      style: { display: "grid", gridTemplateColumns: "2fr 1fr", gap: "20px", marginBottom: "28px" },
    },
      React.createElement(StageFunnelCard, {
        byStage: distributions.by_stage,
        mattersByStage,
        onMatterClick,
        loading: pipeline === null,
        error: pipeline?._error,
      }),
      React.createElement(AttorneyWorkloadCard, {
        byAttorney: distributions.by_attorney,
        mattersByAttorney,
        onMatterClick,
        loading: pipeline === null,
        error: pipeline?._error,
      }),
    ),

    // ─── Quick Actions row ───────────────────────────────────
    React.createElement("div", { style: { marginBottom: "28px" } },
      React.createElement("h2", {
        style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: "14px" },
      }, "Quick Actions"),
      React.createElement("div", {
        style: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px" },
      },
        [
          { icon: I.plus, label: "New Matter",        desc: "Create a new client & matter",   action: () => onNavigate("new-matter") },
          { icon: I.doc,  label: "Generate Document", desc: "Fill a template from Clio data", action: () => onNavigate("generate") },
          { icon: I.folder, label: "Browse Matters",  desc: "View all open matters",          action: () => onNavigate("matters") },
        ].map((item, i) =>
          React.createElement("button", {
            key: i, onClick: item.action,
            style: {
              background: "#fff", border: `1px solid ${C.warmGray}`, borderRadius: "12px",
              padding: "20px 24px", cursor: "pointer", textAlign: "left",
              transition: "all 0.2s", display: "flex", gap: "14px", alignItems: "flex-start",
            },
            onMouseEnter: e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(27,42,74,0.12)"; },
            onMouseLeave: e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; },
          },
            React.createElement("div", {
              style: { width: 40, height: 40, borderRadius: "10px", background: `${C.gold}15`, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, flexShrink: 0 },
            }, item.icon),
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize: "14px", fontWeight: 600, color: C.navy, marginBottom: "3px" } }, item.label),
              React.createElement("div", { style: { fontSize: "12px", color: C.textMuted } }, item.desc),
            ),
          ),
        ),
      ),
    ),

    // ─── Two-column row: Recent Activity + Recent Matters ───
    React.createElement("div", {
      style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" },
    },
      _activityCard({ entries: activity, onNavigate, onMatterClick }),
      _recentMattersCard({
        matters: matters?.recent || [],
        loading: loadingMatters,
        onNavigate, onMatterClick,
      }),
    ),
  );
}

// ─────────────────────────────────────────────────────────────────
// Internal render helpers
// ─────────────────────────────────────────────────────────────────

function _kpiCard({ label, value, sub, onClick, subColor }) {
  return React.createElement("button", {
    onClick,
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, transition: "all 0.2s",
      cursor: onClick ? "pointer" : "default",
      textAlign: "left",
      width: "100%",
    },
    onMouseEnter: e => { if (onClick) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(27,42,74,0.12)"; } },
    onMouseLeave: e => { if (onClick) { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; } },
  },
    React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: "8px" },
    }, label),
    React.createElement("div", {
      style: { fontFamily: "'Playfair Display',serif", fontSize: "32px", fontWeight: 700, color: C.navy, lineHeight: 1 },
    }, value),
    React.createElement("div", {
      style: { fontSize: "12px", color: subColor || C.textLight, marginTop: "6px" },
    }, sub),
  );
}

function _pipelineCard({ summary, loading, error, onClick }) {
  const tiers = ["active", "idle", "aging", "stale"];
  return React.createElement("button", {
    onClick,
    title: "Click for full Pipeline view",
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, transition: "all 0.2s",
      cursor: "pointer", textAlign: "left", width: "100%",
    },
    onMouseEnter: e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(27,42,74,0.12)"; },
    onMouseLeave: e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; },
  },
    React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: "10px" },
    }, "Pipeline Status"),
    loading
      ? React.createElement("div", { style: { padding: "8px 0" } }, React.createElement(Spinner, { size: 18 }))
      : error
        ? React.createElement("div", { style: { fontSize: "13px", color: C.textMuted } }, "Pipeline unavailable")
        : React.createElement("div", { style: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" } },
            tiers.map(tier => {
              const t = TIER_STYLES[tier];
              const count = summary?.[tier] ?? 0;
              return React.createElement("div", {
                key: tier,
                style: { background: t.bg, borderRadius: "8px", padding: "8px 0", textAlign: "center" },
              },
                React.createElement("div", {
                  style: { fontFamily: "'Playfair Display',serif", fontSize: "20px", fontWeight: 700, color: t.color, lineHeight: 1 },
                }, count),
                React.createElement("div", {
                  style: { fontSize: "10px", fontWeight: 700, color: t.color, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: "4px" },
                }, t.label),
              );
            })
          ),
  );
}

function _tasksDueCard({ data, loading, onClick }) {
  // Tasks Due: prominent count with a colored sub-line that calls
  // out overdue vs due-this-week. Most action-driving widget on the
  // dashboard for daily work.
  if (loading) {
    return _kpiCard({ label: "Tasks Due", value: "…", sub: "Loading…" });
  }
  if (data?._error) {
    return _kpiCard({ label: "Tasks Due", value: "—", sub: "Tasks unavailable" });
  }
  const overdue = data?.overdue ?? 0;
  const dueThisWeek = data?.due_this_week ?? 0;
  const value = overdue + dueThisWeek;
  let sub, subColor;
  if (overdue > 0) {
    sub = `${overdue} overdue · ${dueThisWeek} due this week`;
    subColor = C.error;
  } else if (dueThisWeek > 0) {
    sub = `${dueThisWeek} due this week`;
    subColor = C.goldDim;
  } else {
    sub = "Nothing urgent";
    subColor = C.success;
  }
  return _kpiCard({
    label: "Tasks Due",
    value,
    sub,
    subColor,
    onClick,
  });
}

function OutstandingTasksPanel({ data, onMatterClick }) {
  const [showAll, setShowAll] = useState(false);
  const rows = data?.sample || [];

  if (!rows.length) {
    return React.createElement("div", {
      style: { background: "#fff", border: `1px solid ${C.warmGray}`,
               borderRadius: "12px", padding: "20px", marginBottom: "20px",
               fontSize: "13px", color: C.textMuted, lineHeight: 1.55 },
    }, data
        ? "No open tasks came back from Clio."
        : "Loading tasks…");
  }

  const shown = showAll ? rows : rows.slice(0, 12);
  return React.createElement("div", {
    style: { background: "#fff", border: `1px solid ${C.warmGray}`,
             borderRadius: "12px", marginBottom: "20px", overflow: "hidden" },
  },
    React.createElement("div", {
      style: { padding: "13px 18px", borderBottom: `1px solid ${C.warmGray}`,
               fontSize: "12px", color: C.textMuted, textTransform: "uppercase",
               letterSpacing: "0.06em", fontWeight: 600 },
    }, `Outstanding tasks — oldest first (${rows.length})`),

    ...shown.map((t, i) => React.createElement("div", {
      key: t.id || i,
      className: "stack-row",
      style: { display: "flex", gap: "12px", alignItems: "baseline",
               padding: "10px 18px", fontSize: "13px", color: C.textDark,
               borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}55` },
    },
      React.createElement("span", {
        style: { fontSize: "11.5px", color: C.textMuted, minWidth: "92px",
                 fontVariantNumeric: "tabular-nums" },
      }, t.due_at ? String(t.due_at).slice(0, 10) : "no due date"),
      React.createElement("span", { style: { flex: 1, minWidth: 0 } }, t.name),
      t.matter_display && React.createElement("button", {
        onClick: () => onMatterClick && t.matter_id
          && onMatterClick({ id: Number(t.matter_id) }),
        style: { border: "none", background: "transparent", padding: 0,
                 fontSize: "12px", color: C.navy, cursor: "pointer",
                 textDecoration: "underline", flexShrink: 0,
                 maxWidth: "190px", overflow: "hidden",
                 textOverflow: "ellipsis", whiteSpace: "nowrap" },
      }, t.matter_display),
    )),

    rows.length > 12 && React.createElement("button", {
      onClick: () => setShowAll((v) => !v),
      style: { border: "none", background: "transparent", padding: "10px 18px",
               fontSize: "12px", color: C.navy, cursor: "pointer",
               textDecoration: "underline" },
    }, showAll ? "Show fewer" : `Show all ${rows.length}`),
  );
}


function _costsOutCard({ finance, onNavigate }) {
  // The single figure Aaron wants at a glance: what the firm has out
  // on live cases. Clicking goes to Finance, where it opens up.
  if (!finance) return _kpiCard({ label: "Costs Out", value: "…", sub: "Loading…" });
  if (finance._error) {
    // Not zero. A finance figure that renders 0 when the source failed
    // is the most dangerous number on the page.
    return _kpiCard({ label: "Costs Out", value: "—",
                      sub: "Could not be read" });
  }
  const out = finance.totals?.outstanding ?? 0;
  const n = finance.totals?.matters_with_outstanding ?? 0;
  return _kpiCard({
    label: "Costs Out",
    value: out >= 1000 ? `$${Math.round(out / 1000)}k` : `$${Math.round(out)}`,
    sub: `across ${n} matter${n === 1 ? "" : "s"}`,
    onClick: () => onNavigate && onNavigate("finance"),
  });
}


function _aiSpendCard({ cost, calls, loading, error }) {
  // 30-day spend.
  const display = loading
    ? "…"
    : error
      ? "—"
      : (cost != null ? `$${cost.toFixed(2)}` : "$0.00");
  const sub = loading
    ? "Loading…"
    : error
      ? "Usage data unavailable"
      : `${calls ?? 0} API call${calls === 1 ? "" : "s"} · last 30 days`;
  return _kpiCard({
    label: "AI Spend",
    value: display,
    sub,
  });
}

function StageFunnelCard({ byStage, mattersByStage, onMatterClick, loading, error }) {
  // Per-stage horizontal bar list — one row per Clio Matter Stage,
  // ordered most → least (Clio returns count-desc). Each row shows
  // the stage name, a colored bar sized as % of the busiest stage,
  // and the raw count inline. Easier to scan than a single stacked
  // bar where small stages disappear into thin slices.
  //
  // Rows are click-to-expand: clicking a stage reveals the matter
  // list below (from mattersByStage, already loaded via the pipeline
  // fetch — no extra network call). One expanded at a time; clicking
  // an expanded row collapses it. Matter rows link to matter detail
  // via onMatterClick.
  const [expandedStage, setExpandedStage] = useState(null);

  const handleToggle = (stageName) => {
    setExpandedStage(prev => (prev === stageName ? null : stageName));
  };

  return React.createElement("div", {
    style: { background: "#fff", borderRadius: "12px", padding: "20px 24px", border: `1px solid ${C.warmGray}` },
  },
    React.createElement("div", {
      style: {
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        marginBottom: "16px",
      },
    },
      React.createElement("div", {
        style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 },
      }, "Matter Stage Funnel"),
      !loading && !error && byStage && byStage.length > 0 && React.createElement("div", {
        style: { fontSize: "11px", color: C.textLight },
      }, `${byStage.reduce((s, x) => s + (x.count || 0), 0)} total`),
    ),
    loading
      ? React.createElement("div", { style: { padding: "20px 0", textAlign: "center" } }, React.createElement(Spinner, { size: 18 }))
      : error || !byStage || byStage.length === 0
        ? React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, padding: "10px 0" } },
            error ? "Pipeline unavailable" : "No stage data — matters may not have stages assigned.")
        : (() => {
            // Sized relative to the max so small stages remain visible
            const max = Math.max(...byStage.map(s => s.count || 0)) || 1;
            return React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "12px" } },
              byStage.map((s, i) => {
                const pct = (s.count / max) * 100;
                const color = STAGE_BAR_COLORS[i % STAGE_BAR_COLORS.length];
                const isExpanded = expandedStage === s.name;
                const stageMatters = (mattersByStage && mattersByStage[s.name]) || [];

                return React.createElement("div", { key: s.name },
                  // Stage row — button wrapper so it's obviously clickable
                  React.createElement("button", {
                    onClick: () => handleToggle(s.name),
                    title: isExpanded ? "Click to collapse" : `Click to see ${s.count} matter${s.count === 1 ? "" : "s"} in ${s.name}`,
                    style: {
                      width: "100%", padding: "0", border: "none", background: "transparent",
                      cursor: "pointer", textAlign: "left",
                      borderRadius: "6px", transition: "background 0.15s",
                    },
                    onMouseEnter: e => { e.currentTarget.style.background = `${C.warmGray}20`; },
                    onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
                  },
                    // Row 1: chevron + name (left) + count (right)
                    React.createElement("div", {
                      style: {
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        fontSize: "13px", marginBottom: "5px", padding: "2px 4px",
                      },
                    },
                      React.createElement("span", {
                        style: {
                          color: C.navy, fontWeight: 600, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap",
                          display: "flex", alignItems: "center", gap: "6px",
                        },
                      },
                        React.createElement("span", {
                          style: {
                            fontSize: "10px", color: C.textLight,
                            display: "inline-block", width: "10px",
                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.15s",
                          },
                        }, "▸"),
                        s.name,
                      ),
                      React.createElement("span", {
                        style: { color: C.textMuted, marginLeft: "8px", fontVariantNumeric: "tabular-nums", paddingRight: "4px" },
                      }, s.count),
                    ),
                    // Row 2: filled bar
                    React.createElement("div", {
                      style: {
                        height: "10px",
                        background: `${C.warmGray}40`,
                        borderRadius: "5px",
                        overflow: "hidden",
                        margin: "0 4px",
                      },
                    },
                      React.createElement("div", {
                        style: {
                          height: "100%",
                          width: `${Math.max(2, pct)}%`,
                          background: color,
                          borderRadius: "5px",
                          transition: "width 0.3s",
                        },
                      }),
                    ),
                  ),

                  // Expanded matter list — only when this stage is open
                  isExpanded && React.createElement("div", {
                    style: {
                      marginTop: "8px", marginBottom: "4px",
                      padding: "8px", borderRadius: "8px",
                      background: `${C.warmGray}18`,
                      maxHeight: "280px", overflowY: "auto",
                      border: `1px solid ${C.warmGray}60`,
                    },
                  },
                    stageMatters.length === 0
                      ? React.createElement("div", {
                          style: { fontSize: "12px", color: C.textMuted, padding: "6px", textAlign: "center" },
                        }, "No matters to show")
                      : stageMatters.map(m => React.createElement("button", {
                          key: m.matter_id,
                          onClick: (e) => {
                            e.stopPropagation();
                            if (onMatterClick) {
                              onMatterClick({
                                id: m.matter_id,
                                display_number: m.display_number,
                                client: { name: m.client_name },
                              });
                            }
                          },
                          style: {
                            width: "100%", display: "flex", justifyContent: "space-between",
                            alignItems: "center", padding: "6px 8px",
                            border: "none", background: "transparent",
                            cursor: onMatterClick ? "pointer" : "default",
                            textAlign: "left", fontSize: "12px",
                            borderRadius: "4px", transition: "background 0.1s",
                          },
                          onMouseEnter: e => { if (onMatterClick) e.currentTarget.style.background = "#fff"; },
                          onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
                        },
                          React.createElement("span", {
                            style: { color: C.navy, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                          }, m.client_name || `Matter #${m.matter_id}`),
                          React.createElement("span", {
                            style: { color: C.textLight, marginLeft: "10px", fontFamily: "monospace", flexShrink: 0 },
                          }, m.display_number || ""),
                        )),
                  ),
                );
              }),
            );
          })(),
  );
}

function AttorneyWorkloadCard({ byAttorney, mattersByAttorney, onMatterClick, loading, error }) {
  // Compact horizontal bars showing matter count per responsible
  // attorney. Helps surface workload imbalances at a glance.
  //
  // Click-to-expand: same pattern as StageFunnelCard. Clicking an
  // attorney row reveals the matter list below (from mattersByAttorney,
  // already loaded via the pipeline fetch). One expanded at a time;
  // clicking an expanded row collapses it. Matter rows link to
  // matter detail via onMatterClick.
  //
  // Only the top 6 attorneys are shown (existing display limit).
  // Attorneys ranked 7+ don't appear at all — no expand affordance
  // for hidden rows.
  const [expandedAttorney, setExpandedAttorney] = useState(null);

  const handleToggle = (attorneyName) => {
    setExpandedAttorney(prev => (prev === attorneyName ? null : attorneyName));
  };

  return React.createElement("div", {
    style: { background: "#fff", borderRadius: "12px", padding: "20px 24px", border: `1px solid ${C.warmGray}` },
  },
    React.createElement("div", {
      style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: "16px" },
    }, "Attorney Workload"),
    loading
      ? React.createElement("div", { style: { padding: "20px 0", textAlign: "center" } }, React.createElement(Spinner, { size: 18 }))
      : error || !byAttorney || byAttorney.length === 0
        ? React.createElement("div", { style: { fontSize: "13px", color: C.textMuted, padding: "10px 0" } },
            "No attorney data")
        : (() => {
            const max = Math.max(...byAttorney.map(a => a.count || 0)) || 1;
            return React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "10px" } },
              byAttorney.slice(0, 6).map(a => {
                const pct = (a.count / max) * 100;
                const isExpanded = expandedAttorney === a.name;
                const attorneyMatters = (mattersByAttorney && mattersByAttorney[a.name]) || [];

                return React.createElement("div", { key: a.name },
                  // Attorney row — button wrapper so it's clickable
                  React.createElement("button", {
                    onClick: () => handleToggle(a.name),
                    title: isExpanded ? "Click to collapse" : `Click to see ${a.count} matter${a.count === 1 ? "" : "s"} for ${a.name}`,
                    style: {
                      width: "100%", padding: "0", border: "none", background: "transparent",
                      cursor: "pointer", textAlign: "left",
                      borderRadius: "6px", transition: "background 0.15s",
                    },
                    onMouseEnter: e => { e.currentTarget.style.background = `${C.warmGray}20`; },
                    onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
                  },
                    React.createElement("div", {
                      style: { display: "flex", justifyContent: "space-between", fontSize: "12px", marginBottom: "4px", padding: "2px 4px" },
                    },
                      React.createElement("span", {
                        style: {
                          color: C.navy, fontWeight: 600, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap",
                          display: "flex", alignItems: "center", gap: "6px",
                        },
                      },
                        React.createElement("span", {
                          style: {
                            fontSize: "10px", color: C.textLight,
                            display: "inline-block", width: "10px",
                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.15s",
                          },
                        }, "▸"),
                        a.name,
                      ),
                      React.createElement("span", {
                        style: { color: C.textMuted, marginLeft: "8px", paddingRight: "4px" },
                      }, a.count),
                    ),
                    React.createElement("div", {
                      style: { height: "8px", background: `${C.warmGray}40`, borderRadius: "4px", overflow: "hidden", margin: "0 4px" },
                    },
                      React.createElement("div", {
                        style: {
                          height: "100%", width: `${pct}%`,
                          background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
                          transition: "width 0.3s",
                        },
                      }),
                    ),
                  ),

                  // Expanded matter list — only when this attorney is open
                  isExpanded && React.createElement("div", {
                    style: {
                      marginTop: "8px", marginBottom: "4px",
                      padding: "8px", borderRadius: "8px",
                      background: `${C.warmGray}18`,
                      maxHeight: "280px", overflowY: "auto",
                      border: `1px solid ${C.warmGray}60`,
                    },
                  },
                    attorneyMatters.length === 0
                      ? React.createElement("div", {
                          style: { fontSize: "12px", color: C.textMuted, padding: "6px", textAlign: "center" },
                        }, "No matters to show")
                      : attorneyMatters.map(m => React.createElement("button", {
                          key: m.matter_id,
                          onClick: (e) => {
                            e.stopPropagation();
                            if (onMatterClick) {
                              onMatterClick({
                                id: m.matter_id,
                                display_number: m.display_number,
                                client: { name: m.client_name },
                              });
                            }
                          },
                          style: {
                            width: "100%", display: "flex", justifyContent: "space-between",
                            alignItems: "center", padding: "6px 8px",
                            border: "none", background: "transparent",
                            cursor: onMatterClick ? "pointer" : "default",
                            textAlign: "left", fontSize: "12px",
                            borderRadius: "4px", transition: "background 0.1s",
                          },
                          onMouseEnter: e => { if (onMatterClick) e.currentTarget.style.background = "#fff"; },
                          onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
                        },
                          React.createElement("span", {
                            style: { color: C.navy, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                          }, m.client_name || `Matter #${m.matter_id}`),
                          React.createElement("span", {
                            style: { color: C.textLight, marginLeft: "10px", fontFamily: "monospace", flexShrink: 0 },
                          }, m.display_number || ""),
                        )),
                  ),
                );
              }),
            );
          })(),
  );
}

function _activityCard({ entries, onNavigate, onMatterClick }) {
  return React.createElement("div", null,
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" },
    },
      React.createElement("h2", {
        style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 },
      }, "Recent Activity"),
      React.createElement("button", {
        onClick: () => onNavigate("audit-log"),
        style: { border: "none", background: "none", cursor: "pointer", fontSize: "13px", color: C.gold, fontWeight: 500 },
      }, "View all →"),
    ),
    React.createElement("div", {
      style: { background: "#fff", borderRadius: "12px", border: `1px solid ${C.warmGray}`, overflow: "hidden", minHeight: "200px" },
    },
      entries === null
        ? React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null))
        : entries.length === 0
          ? React.createElement("div", { style: { padding: "40px", textAlign: "center", color: C.textMuted, fontSize: "14px" } }, "No recent activity.")
          : entries.slice(0, 7).map((e, i) => {
              const cfg = EVENT_TYPE_CONFIG[e.event_type] || { label: e.event_type, variant: "default" };
              const clickable = !!e.matter_id;
              return React.createElement("div", {
                key: e.id || i,
                onClick: clickable ? () => onMatterClick({ id: Number(e.matter_id), client: { name: e.matter_name } }) : null,
                style: {
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "12px 16px",
                  borderBottom: i < Math.min(entries.length, 7) - 1 ? `1px solid ${C.warmGray}40` : "none",
                  cursor: clickable ? "pointer" : "default",
                  transition: "background 0.15s",
                },
                onMouseEnter: ev => { if (clickable) ev.currentTarget.style.background = C.warmWhite; },
                onMouseLeave: ev => { ev.currentTarget.style.background = "transparent"; },
              },
                React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                  React.createElement("div", {
                    style: { fontSize: "13px", color: C.navy, fontWeight: 500, marginBottom: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                  }, e.description || cfg.label),
                  React.createElement("div", {
                    style: { fontSize: "11px", color: C.textMuted, display: "flex", gap: "8px", alignItems: "center" },
                  },
                    e.user_name && React.createElement("span", null, e.user_name),
                    e.matter_name && React.createElement("span", { style: { color: C.gold } }, "· ", e.matter_name),
                    React.createElement("span", { style: { color: C.textLight } }, "· ", formatAuditTime(e.created_at, true)),
                  ),
                ),
                React.createElement("div", { style: { flexShrink: 0 } },
                  React.createElement(Badge, { variant: cfg.variant }, cfg.label),
                ),
              );
            }),
    ),
  );
}

function _recentMattersCard({ matters, loading, onNavigate, onMatterClick }) {
  return React.createElement("div", null,
    React.createElement("div", {
      style: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" },
    },
      React.createElement("h2", {
        style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 },
      }, "Recent Matters"),
      React.createElement("button", {
        onClick: () => onNavigate("matters"),
        style: { border: "none", background: "none", cursor: "pointer", fontSize: "13px", color: C.gold, fontWeight: 500 },
      }, "View all →"),
    ),
    React.createElement("div", {
      style: { background: "#fff", borderRadius: "12px", border: `1px solid ${C.warmGray}`, overflow: "hidden", minHeight: "200px" },
    },
      loading
        ? React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null))
        : matters.length === 0
          ? React.createElement("div", { style: { padding: "40px", textAlign: "center", color: C.textMuted, fontSize: "14px" } }, "No open matters found.")
          : matters.map((m, i) =>
              React.createElement("button", {
                key: m.id,
                onClick: () => onMatterClick(m),
                style: {
                  width: "100%", display: "flex", alignItems: "center", padding: "14px 20px",
                  border: "none", background: "transparent", cursor: "pointer", textAlign: "left",
                  borderBottom: i < matters.length - 1 ? `1px solid ${C.warmGray}40` : "none",
                  transition: "background 0.15s",
                },
                onMouseEnter: e => { e.currentTarget.style.background = C.warmWhite; },
                onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
              },
                React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                  React.createElement("div", {
                    style: { fontSize: "14px", fontWeight: 600, color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                  }, m.client?.name || `Matter #${m.id}`),
                  React.createElement("div", { style: { fontSize: "12px", color: C.textMuted, marginTop: "2px" } },
                    m.display_number || ""),
                ),
                React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 } },
                  m.status && React.createElement(Badge, { variant: "gold" }, m.status),
                  React.createElement("span", { style: { color: C.textLight } }, I.chevron),
                ),
              ),
            ),
    ),
  );
}
